

import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { MeetingPreviewSkeleton } from "../../components/LoadingShimmer";
import { usePreviewData } from "./hooks/usePreviewData";
import { useAtaImagens } from "./hooks/useAtaImagens";
import { PreviewHeader } from "./components/PreviewHeader";
import { AtaHeader } from "./components/AtaHeader";
import { TopicPreviewSection } from "./components/TopicPreviewSection";
import { AtaGaleriaPreview } from "./components/AtaGaleriaPreview";
import { PreviewFooter } from "./components/PreviewFooter";
import { ContatosSection } from "./components/ContatosSection";
import { useContatosProjeto } from "../../hooks/useContatosProjeto";
import { Header } from "../../components/Header";
import {
  CustomizeFilenameDialog,
  type PdfDevelopmentsLimit,
} from "../../components/CustomizeFilenameDialog";
import { translateMeetingData, type OrganizedDataForTranslation } from "../../services/translation.service";
import type { SupportedLanguage } from "../../i18n/config";
import type { Meeting } from "../../services/meeting.service";
import { usePageTitle } from "../../hooks/usePageTitle";

export default function MeetingPreview() {
  const { i18n } = useTranslation('common');
  const { id: projectId, meetingId } = useParams<{
    id: string;
    meetingId: string;
  }>();

  const [showFilenameDialog, setShowFilenameDialog] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState<{ current: number; total: number } | null>(null);
  const [translatedMeeting, setTranslatedMeeting] = useState<Meeting | null>(null);
  const [translatedOrganizedData, setTranslatedOrganizedData] = useState<OrganizedDataForTranslation | null>(null);
  const [pdfDevelopmentsLimit, setPdfDevelopmentsLimit] =
    useState<PdfDevelopmentsLimit>(null);

  const {
    meeting,
    project,
    participantes,
    organizedData,
    loading,
    error,
  } = usePreviewData({
    projectId: projectId || "",
    meetingId: meetingId || "",
  });

  const { imagens } = useAtaImagens({
    ataId: meetingId || "",
  });

  const { contatos } = useContatosProjeto(projectId);
  usePageTitle(meeting ? `Preview — ${meeting.name || `Ata #${meeting.number}`}` : 'Preview da Ata');

  const handleExportPDF = async (
    customFilename: string,
    exportLanguage: SupportedLanguage,
    developmentsLimit: PdfDevelopmentsLimit = null
  ) => {
    if (!meeting || !project) return;

    setPdfDevelopmentsLimit(developmentsLimit);

    const originalTitle = document.title;
    const currentLanguage = (i18n.language as SupportedLanguage) || 'pt-BR';
    const needsTranslation = exportLanguage !== currentLanguage;

    if (needsTranslation) {
      setIsTranslating(true);
      try {
        const translated = await translateMeetingData(
          meeting,
          organizedData,
          currentLanguage,
          exportLanguage,
          (current, total) => setTranslationProgress({ current, total })
        );
        setTranslatedMeeting(translated.meeting);
        setTranslatedOrganizedData(translated.organizedData);
        await i18n.changeLanguage(exportLanguage);
      } catch (err) {
        console.error('Falha na tradução, prosseguindo sem traduzir:', err);
        await i18n.changeLanguage(exportLanguage);
      }
      setIsTranslating(false);
      setTranslationProgress(null);
    }

    document.title = customFilename;

    setTimeout(() => {
      const imagesToLoad = document.querySelectorAll('img[src]');
      const imagePromises = Array.from(imagesToLoad).map((img) => {
        return new Promise((resolve) => {
          if ((img as HTMLImageElement).complete) {
            resolve(true);
          } else {
            img.addEventListener('load', () => resolve(true));
            img.addEventListener('error', () => resolve(false));
            setTimeout(() => resolve(false), 3000);
          }
        });
      });

      Promise.all(imagePromises).then(() => {
        const html = document.documentElement;
        const wasDark = html.classList.contains('dark');
        if (wasDark) html.classList.remove('dark');

        window.print();

        setTimeout(() => {
          if (wasDark) html.classList.add('dark');
          document.title = originalTitle;
          if (needsTranslation) {
            i18n.changeLanguage(currentLanguage);
            setTranslatedMeeting(null);
            setTranslatedOrganizedData(null);
          }
        }, 1000);
      });
    }, 300);
  };

  const handleOpenExportDialog = () => {
    setShowFilenameDialog(true);
  };

  if (!projectId || !meetingId) {
    return <MeetingPreviewSkeleton />;
  }

  if (loading || !meeting || !project) {
    return <MeetingPreviewSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Recarregar Página
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 20mm 15mm;
          }

          /* Oculta cabeçalhos e rodapés do navegador */
          body {
            margin: 0 !important;
            padding: 0 !important;
          }

          header, footer {
            display: block !important;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box;
          }

          html, body {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }

          body {
            font-size: 9pt;
            line-height: 1.3;
            zoom: 1.00;
          }


          .no-print,
          .preview-header {
            display: none !important;
          }


          .print-container,
          .preview-content,
          .preview-content > div {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }

          .print-container {
            display: block !important;
            width: 100%;
            max-width: 100%;
          }

          /*
           * O componente shadcn <Table> envolve a <table> num
           * <div class="relative w-full overflow-auto">. No motor de impressão
           * do Chrome, uma tabela dentro de um container com overflow não pagina
           * corretamente: as linhas que ultrapassam o limite da página (em geral
           * os desenvolvimentos no fim de um item) simplesmente NÃO são
           * renderizadas na página seguinte — daí "alguns tópicos não mostram o
           * desenvolvimento na impressão". display:contents remove o box do
           * wrapper para a tabela fluir direto no documento e paginar/repetir o
           * thead corretamente. As tabelas de contatos/galeria usam <table> cru
           * (sem wrapper) e por isso já funcionavam.
           */
          .print-container .overflow-auto:has(> table) {
            display: contents !important;
          }

          .min-h-screen {
            min-height: auto !important;
          }

          .preview-content {
            padding: 0 !important;
            margin: 0 !important;
          }


          header {
            display: block !important;
            page-break-inside: avoid;
            margin-bottom: 12pt;
          }

          header h1 {
            display: block !important;
            font-size: 14pt !important;
            font-weight: bold !important;
            margin-bottom: 6pt !important;
          }

          header img {
            display: block !important;
            max-height: 40pt !important;
            max-width: 150pt !important;
          }

          header .grid {
            display: grid !important;
          }


          h1 {
            font-size: 14pt;
            margin: 0 0 8pt 0;
            line-height: 1.2;
          }

          h2 {
            font-size: 12pt;
            margin: 8pt 0 6pt 0;
            line-height: 1.2;
          }

          h3 {
            font-size: 10pt;
            margin: 6pt 0 4pt 0;
            line-height: 1.2;
          }

          p, td, th, span, div, li {
            font-size: 8pt;
            line-height: 1.3;
          }


          table {
            width: 100%;
            max-width: 100%;
            border-collapse: collapse;
            font-size: 8pt;
            page-break-inside: auto;
            table-layout: fixed;
          }

          thead {
            display: table-header-group;
          }

          tr {
            page-break-after: auto;
          }

          thead tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          th {
            padding: 4pt 3pt;
            font-size: 7pt;
            font-weight: bold;
            text-align: center;
            background-color: #f3f4f6 !important;
            border: 0.5pt solid #d1d5db;
            word-wrap: break-word;
            overflow-wrap: break-word;
            hyphens: auto;
          }

          td {
            padding: 4pt 3pt;
            font-size: 8pt;
            border: 0.5pt solid #d1d5db;
            vertical-align: top;
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
            white-space: normal !important;
            max-width: 100%;
          }

          th, td {
            overflow: visible;
          }


          .mb-6, .mb-8 {
            margin-bottom: 6pt !important;
          }

          .mb-10 {
            margin-bottom: 12pt !important;
          }

          .mb-4 {
            margin-bottom: 6pt !important;
          }

          .mb-2 {
            margin-bottom: 4pt !important;
          }

          .mb-3 {
            margin-bottom: 8pt !important;
          }

          .pb-0\.5 {
            padding-bottom: 1pt !important;
          }

          .p-4, .p-6, .p-8 {
            padding: 4pt !important;
          }

          .space-y-6 > * + * {
            margin-top: 6pt !important;
          }

          .space-y-2 > * + * {
            margin-top: 3pt !important;
          }


          .bg-gray-50 {
            background-color: #f9fafb !important;
          }

          .bg-yellow-50 {
            background-color: #fefce8 !important;
          }

          .bg-green-100 {
            background-color: #dcfce7 !important;
          }

          .border-yellow-400 {
            border-color: #facc15 !important;
          }

          .border-l-4 {
            border-left-width: 3pt !important;
          }


          img {
            max-height: 40pt;
            max-width: 150pt;
            height: auto;
            width: auto;
          }

          /* Imagens da galeria: ocupam página inteira */
          .galeria-table img {
            max-height: none !important;
            max-width: 100% !important;
            width: 100% !important;
            height: auto !important;
            object-fit: contain !important;
          }


          .grid {
            display: grid;
            gap: 4pt;
          }


          table:not(.contatos-table) th:nth-child(1),
          table:not(.contatos-table) td:nth-child(1) {
            width: 5%;
            max-width: 10mm;
          }

          table:not(.contatos-table) th:nth-child(2),
          table:not(.contatos-table) td:nth-child(2) {
            width: 7%;
            max-width: 13mm;
          }

          table:not(.contatos-table) th:nth-child(3),
          table:not(.contatos-table) td:nth-child(3) {
            width: 12%;
            max-width: 25mm;
            white-space: nowrap;
          }

          table:not(.contatos-table) th:nth-child(4),
          table:not(.contatos-table) td:nth-child(4) {
            width: 47%;
            min-width: 80mm;
          }

          table:not(.contatos-table) th:nth-child(5),
          table:not(.contatos-table) td:nth-child(5) {
            width: 14%;
            max-width: 25mm;
          }

          table:not(.contatos-table) th:nth-child(6),
          table:not(.contatos-table) td:nth-child(6) {
            width: 15%;
            max-width: 27mm;
          }

          /* Tabela de contatos: colunas com larguras adequadas */
          .contatos-table th:nth-child(1),
          .contatos-table td:nth-child(1) {
            width: 22%;
            white-space: normal;
          }

          .contatos-table th:nth-child(2),
          .contatos-table td:nth-child(2) {
            width: 14%;
            white-space: normal;
          }

          .contatos-table th:nth-child(3),
          .contatos-table td:nth-child(3) {
            width: 18%;
            white-space: normal;
          }

          .contatos-table th:nth-child(4),
          .contatos-table td:nth-child(4) {
            width: 18%;
            white-space: nowrap;
          }

          .contatos-table th:nth-child(5),
          .contatos-table td:nth-child(5) {
            width: 28%;
            word-break: break-all;
          }


          .page-break-inside-avoid {
            page-break-inside: avoid;
            break-inside: avoid;
          }


          .shadow-sm,
          .shadow-md {
            box-shadow: none !important;
          }


          * {
            overflow: visible !important;
          }

          /* Reduzir espaçamentos gerais */
          section {
            margin-bottom: 8pt !important;
          }

          .mt-6, .mt-8 {
            margin-top: 6pt !important;
          }
        }

        @media screen {
          .print-container {
            width: 100%;
          }
        }
      `}</style>

      {}
      <div className="no-print">
        <Header
          breadcrumbs={[
            { label: 'Meus Projetos', href: '/dashboard' },
            { label: project?.name || 'Projeto', href: `/projects/${projectId}/meetings` },
            { label: `${meeting?.name ? `${meeting.name} ${meeting.number}` : `Ata ${meeting.number || ''}`} (Visualização)` }
          ]}
        />
      </div>

      <div className="min-h-screen bg-gray-100 print:bg-white print:min-h-0">
        {}
        <PreviewHeader
          projectId={projectId}
          meetingId={meetingId}
          meeting={meeting}
          project={project}
          onOpenExportDialog={handleOpenExportDialog}
          isTranslating={isTranslating}
        />

        {}
        <div className="preview-content py-4 px-4 md:py-8 md:px-12 print:!p-0 print:!m-0">
          <div className="print-container max-w-[1200px] mx-auto bg-white shadow-sm p-6 md:p-8 print:shadow-none print:!p-4 print:!m-0 print:!max-w-none print:!w-full flex flex-col min-h-screen print:min-h-[calc(100vh-40mm)]">

            {}
            {meeting.status !== "final" && (
              <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 print:hidden">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700 font-medium">
                      <strong>ATENÇÃO:</strong> Esta ata está em andamento e ainda não foi finalizada. O conteúdo pode sofrer alterações.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {}
            <AtaHeader
              meeting={translatedMeeting || meeting}
              project={project}
              participantes={participantes}
            />

            {}
            <section className="space-y-6">
              {(translatedOrganizedData || organizedData).topicos.map((topico, topicIdx) => (
                <TopicPreviewSection
                  key={topico.id}
                  topico={topico}
                  items={topico.items}
                  topicIndex={topicIdx}
                  meetingNumber={meeting.number}
                  developmentsLimit={pdfDevelopmentsLimit}
                />
              ))}

              {}
              {organizedData.topicos.length === 0 && (
                  <section className="mb-6">
                    <p className="text-gray-500 italic">
                      Nenhum item de ação registrado nesta ata.
                    </p>
                  </section>
                )}
            </section>

            {/* Galeria de Imagens */}
            <AtaGaleriaPreview imagens={imagens} />

            {/* Contatos da Obra */}
            <ContatosSection contatos={contatos} />

            {}
            <PreviewFooter meeting={translatedMeeting || meeting} project={project} />
          </div>
        </div>
      </div>

      {/* Overlay de tradução */}
      {isTranslating && (
        <div className="fixed inset-0 bg-white/75 z-50 flex items-center justify-center no-print">
          <div className="flex flex-col items-center gap-3 p-6 bg-white rounded-xl shadow-lg border border-border" style={{ minWidth: 240 }}>
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
            <p className="text-sm font-medium text-gray-700">Traduzindo conteúdo...</p>
            {translationProgress && translationProgress.total > 0 ? (
              <>
                <p className="text-xs text-gray-500">
                  {translationProgress.current} / {translationProgress.total} campos
                </p>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${Math.round((translationProgress.current / translationProgress.total) * 100)}%` }}
                  />
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-400">Isso pode levar alguns segundos</p>
            )}
          </div>
        </div>
      )}

      <CustomizeFilenameDialog
        open={showFilenameDialog}
        onClose={() => setShowFilenameDialog(false)}
        onExport={handleExportPDF}
        projectCode={project.project_code}
        meetingNumber={meeting.number}
        meetingDate={meeting.date}
        isMultipleMeetings={false}
        currentLanguage={(i18n.language as SupportedLanguage) || 'pt-BR'}
        showDevelopmentsLimitOption
        defaultDevelopmentsLimit={pdfDevelopmentsLimit}
      />
    </>
  );
}

